import { useSuspenseQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { When } from 'react-if';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import RateAmount from '@/components/app/product/components/price-info/rate-amount';
import SalesAmount from '@/components/app/product/components/price-info/sales-amount';
import Props from '@/components/app/product/components/price-info/type';
import SuggestedAlternativeProducts from '@/components/app/product/components/suggested-alternative-products';
import { getDefaultPricing, isBundle } from '@/components/app/product/helpers';
import { getSingleProductOptions } from '@/components/app/product/services/hooks';
import PriceView from '@/components/shared/components/price-view';

import { usePrerequisitesStore } from '@/store/prerequisites.store';

export default function PriceInfo({ slug }: Props) {
  const { data } = useSuspenseQuery(getSingleProductOptions(slug));
  const { rate, saleAmount, isOnSale, saleEndedAt, sellable } = data;
  const { price, lastPrice, discount } = getDefaultPricing(data);
  const finalDiscount = (isOnSale && discount) || 0;
  const { prerequisites } = usePrerequisitesStore();
  const [finalPrice, setFinalPrice] = useState(price);
  const [finalLastPrice, setFinalLastPrice] = useState(lastPrice);

  useEffect(() => {
    if (data && Object.keys(prerequisites).length > 0) {
      let calculatedPrice = price;
      let calculatedLastPrice = lastPrice;

      for (const prerequisite of Object.values(prerequisites)) {
        if (!prerequisite.selected) {
          calculatedPrice -= prerequisite.price;
          if (lastPrice > 0) {
            calculatedLastPrice -= prerequisite.price;
          }
        }
      }

      setFinalPrice(calculatedPrice);
      setFinalLastPrice(calculatedLastPrice);
    }
  }, [price, lastPrice, prerequisites]);

  return (
    <Container
      id='productInfo'
      className='relative bottom-8 z-10 mx-auto px-4 md:static md:m-0 md:pt-6 md:pr-8 md:pl-0'
    >
      <Container
        center
        className='flex flex-col rounded-b-md bg-[#FFFFFF] p-1 shadow-[0px_10px_25px_0px_#CECECE40] md:p-0 md:shadow-none'
      >
        <Container className='flex w-full columns-3 items-center justify-between'>
          {rate && <RateAmount rate={rate} className='p-4' />}
          <SalesAmount value={saleAmount} isFree={price === 0} />

          <PriceView
            sellable={sellable}
            price={finalPrice}
            lastPrice={finalLastPrice}
            saleEndedAt={saleEndedAt}
            showDiscountWithoutExpiration={isBundle(data)}
            discount={finalDiscount}
            showDiscountLabel={true}
            className={cn(
              'productPrice relative flex h-full items-center gap-3 rounded-s-md bg-[linear-gradient(111.32deg,_#FFDAA266_-1.54%,_#FFF3E066_21.68%)] px-7 pt-[22px] pb-[17px] md:p-5 md:pr-10',
              finalDiscount > 0 || (isBundle(data) && 'pt-7 pb-3.5'),
            )}
            priceWrapperClassName='flex flex-col items-end'
            priceClassName={cn(
              'text-[22px]! md:text-[25px]! font-bold productRegPrice',
              !finalDiscount && 'flex-col',
            )}
            discountLabelClassName='font-bold! text-[17px]! absolute md:top-[20%] md:-right-[100%] inset-x-0 -top-4 mx-auto border-[#FFFFFF] border-[2px] w-10 h-10 shadow-[0px_4px_12px_-5px_#FF7B016E]'
            priceCurrencyWrapperClassName='flex-col items-end gap-0'
            lastPriceClassName='text-[17px]'
            currencyClassName='text-[#BFBFBF]'
            regularPrice={finalLastPrice}
          />
        </Container>
        <Container className='w-full md:hidden'>
          <When condition={!data.sellable && data.alternativeSuggestions.length > 0}>
            <SuggestedAlternativeProducts data={data.alternativeSuggestions} />
          </When>
        </Container>
      </Container>
    </Container>
  );
}
